/**
 * The shapes the API returns, in one place.
 *
 * Three screens read a trip — the book, the object page and the shortlist — and
 * when each declared its own row type they drifted within a week: one knew about
 * `kind`, one did not, and the bug looked like a missing pill rather than a
 * missing field. One declaration, three importers.
 *
 * Deliberately types only. The catalogue itself (`/trip-kinds`) is data from the
 * server so the questions, cost lines and pack lists cannot differ between the
 * two halves of the app.
 */

export type TripStatus = "idea" | "planned" | "done";

export type Trip = {
  id: string;
  name: string;
  status: TripStatus;
  kind: string | null;
  kindLabel: string | null;
  from: string | null;
  to: string | null;
  nights: number | null;
  blurb: string;
  vibe: string | null;
  people: number | null;
  budgetZar: number | null;
  /** What was actually spent, filled in after the trip. Not the plan. */
  totalCost: number | null;
  driveKm: number | null;
  driveMinutes: number | null;
  rating: number | null;
  notes: string | null;
  plannedTotal: number | null;
  costBlanks: number | null;
  unanswered: number | null;
};

export type CostLine = { id: string; label: string; hint: string; derived?: "fuel"; perNight?: boolean };
export type Question = { id: string; ask: string; no: string };
export type PackItem = { id: string; label: string; group: string };

export type TripKind = {
  id: string;
  label: string;
  sentence: string;
  nights: boolean;
  costs: CostLine[];
  questions: Question[];
  pack: PackItem[];
};

export type AnsweredQuestion = Question & { answer: "yes" | "no" | null };

export type PricedLine = {
  id: string;
  label: string;
  hint: string;
  amount: number | null;
  derived: boolean;
  perNight: boolean;
  blankBecause: string | null;
};

export type Priced = {
  lines: PricedLine[];
  total: number | null;
  blanks: number;
  perNight: number | null;
  fuelPricePerL: number | null;
  consumptionLPer100km: number | null;
};

export type HistoryEntry = { at: string | null; text: string; actor: string; machine: boolean };

export type TripDetail = {
  trip: Trip;
  kind: TripKind | null;
  questions: AnsweredQuestion[];
  costs: Priced | null;
  history: HistoryEntry[];
};

export type PackRow = {
  id: string;
  label: string;
  group: string;
  packed: boolean;
  source: "base" | "learned" | "own";
  forgottenLastTime: boolean;
  forgottenThisTime: boolean;
  seenOn: number;
  note: string | null;
};

export type PackList = {
  kind: string | null;
  kindLabel?: string;
  rows: PackRow[];
  basis: Array<{ id: string; name: string; from: string | null }>;
  sentence: string;
};

// ── F16 · the away profile ──────────────────────────────────────────────────
//
// The states are the server's, verbatim, and the client adds nothing to them.
// That is deliberate: `armed` is the one word on this screen that must never be
// produced by the browser, because the browser has no way of knowing whether
// anything on the far side moved. See `server/src/away.ts`.

export type AskState = "armed" | "refused" | "unreachable" | "not-connected";

export type Ask = {
  id: string;
  label: string;
  /** What this would do to the house, in one sentence. */
  wants: string;
  state: AskState;
  why: string;
  /** Null only when the ask is genuinely through. */
  needs: string | null;
  at: string | null;
};

export type AwayState = "no-dates" | "not-armed" | "declared" | "partly" | "armed";

export type AwayProfile = {
  state: AwayState;
  headline: string;
  from: string | null;
  to: string | null;
  passExpiresAt: string | null;
  armedAt: string | null;
  armedBy: string | null;
  standDownAt: string | null;
  asks: Ask[];
  through: number;
  total: number;
  canArm: boolean;
  blockedBecause: string | null;
  /** Set only by the stand-down reply, when a pass had genuinely issued. */
  passStillOpen?: boolean;
};

export type Place = {
  name: string;
  kind: string | null;
  kindLabel: string | null;
  minutes: number | null;
  km: number | null;
  timesBeen: number;
  lastVisited: string | null;
  bestRating: number | null;
  tripId: string;
};

export type Band = { id: string; label: string; fits: string; minNights: number | null; places: Place[] };

export type Weekend = {
  from: string;
  to: string;
  nights: number;
  long: boolean;
  because: string[];
  label: string;
  free: boolean;
  takenBy: { id: string; name: string } | null;
};

export type Shortlist = { bands: Band[]; unknown: Place[]; weekends: Weekend[]; note: string };

/** "2h 40" from minutes. Em dash when there is nothing recorded — never "0h00". */
export function driveTime(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}` : `${m} min`;
}
